/**
 * client/src/components/layout/app-shell-header.tsx
 *
 * Top header rendered between the forensic-pause banner and <main>.
 * Always carries the "acting as <profile>" chip on the left; mounts the
 * notification bell + drawer pair on the right when gate conditions pass.
 *
 * The chip sits OUTSIDE the bell gate on purpose — the previously-empty
 * header (a bare border in the personal / AAL1 / platform cases) is exactly
 * where a user is least sure which profile they are on.
 *
 * Gate: (AAL2|AAL3) AND NOT /admin/platform/*.
 * AAL is threaded from AppShellLayout (NOT re-fetched here).
 *
 * The gate NO LONGER requires org context. It previously did — which meant a
 * person acting in their PERSONAL context had no notification surface at all,
 * including for a critical OSINT alert about their own account, the one item
 * that must reach them whichever hat they are wearing. The feed is org-scoped
 * when an org is active and personal-scoped otherwise; the server decides which
 * sources run (`buildNotificationFeed`).
 */

import { useMemo, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { useLocation } from "wouter";
import { useOrgContext } from "@/lib/use-org-context";
import { useCurrentUser } from "@/lib/use-current-user";
import { FORENSIC_PAUSE_BANNER_ID } from "@/components/forensic-pause-banner";
import { NotificationBell, NotificationDrawer } from "@/components/notifications";
import { topItems } from "@/components/notifications/notification-drawer";
import { useNotifications } from "@/lib/use-notifications";
import { isPlatformRoute } from "@/lib/route-context";
import { ProfileContextChip } from "./profile-context-chip";

interface AppShellHeaderProps {
  readonly aal?: "AAL1" | "AAL2" | "AAL3";
  readonly forensicPauseActive?: boolean;
}

/** Rendered only when the gate is open — safe to call useNotifications here. */
export function BellWithDrawer({
  orgId,
  scopePending,
  forensicPauseActive = false,
}: {
  orgId: string | null;
  /** Org context whose id has not resolved — hold in loading, never show empty. */
  scopePending: boolean;
  forensicPauseActive?: boolean;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const { items, actionable, degraded, isLoading } = useNotifications(
    scopePending ? null : orgId,
    { enabled: !scopePending },
  );

  const rows = useMemo(() => topItems(items), [items]);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <NotificationBell
          // Badge the ACTIONABLE count, not every item: an `info` row (an
          // unaccepted invite) is waiting on someone else and can live for 7
          // days, so badging it would light a permanent dot the user cannot
          // clear. Such rows still LIST in the drawer.
          total={actionable}
          degraded={degraded}
          isOpen={isOpen}
          onToggle={() => setIsOpen((v) => !v)}
          forensicPauseActive={forensicPauseActive}
          forensicPauseBannerId={
            forensicPauseActive ? FORENSIC_PAUSE_BANNER_ID : undefined
          }
        />
      </Popover.Trigger>
      <NotificationDrawer
        rows={rows}
        // While the org scope is unresolved the feed is not yet meaningful —
        // show the skeleton, never the empty state.
        isLoading={isLoading || scopePending}
        degraded={degraded}
        forensicPauseActive={forensicPauseActive}
        forensicPauseBannerId={forensicPauseActive ? FORENSIC_PAUSE_BANNER_ID : undefined}
        onClose={() => setIsOpen(false)}
      />
    </Popover.Root>
  );
}

export function AppShellHeader({ aal, forensicPauseActive = false }: AppShellHeaderProps): JSX.Element {
  const { currentContext, activeOrgId } = useOrgContext();
  const { user } = useCurrentUser();
  const [location] = useLocation();

  // Org context is NOT required — see the module header. AAL2+ still is: the
  // feed route itself is `requireAAL("AAL2")`, so rendering the bell below that
  // would only ever produce a 401.
  // A guardian-only shell is CONTAINED away from /api/notifications by the
  // default-deny allowlist (ledger #16), so the feed 403s for it by design.
  // Rendering the bell anyway would leave it permanently in the "could not be
  // loaded" state — a fault signal for what is actually a deliberate boundary.
  // Suppress it instead; the shell still receives `self_notice` WebSocket
  // events about its own account, which is the channel meant for that.
  const guardianOnly = user?.guardianOnly === true;

  const gateOpen =
    (aal === "AAL2" || aal === "AAL3") && !isPlatformRoute(location) && !guardianOnly;

  // Org context whose id has NOT resolved yet (hydration in flight, or the
  // known org-context hydration failure). Falling through to the personal feed
  // here would show an org admin with open approvals a confident empty bell —
  // the very bug this work fixes, re-entered through a new door. Hold the bell
  // in its loading state until the id lands.
  const orgScopePending = currentContext === "org" && activeOrgId === null;

  // Null in personal context — the server then runs only the personal sources.
  const feedOrgId = currentContext === "org" ? activeOrgId : null;

  // Was `header-empty` — renamed because the header is no longer empty when the
  // bell gate is shut; it still carries the profile chip. (Grepped: nothing
  // referenced the old id.)
  if (!gateOpen) {
    return (
      <header
        className="h-11 border-b border-border-subtle flex items-center px-4"
        data-testid="header-bell-gate-closed"
      >
        <ProfileContextChip />
      </header>
    );
  }

  return (
    <header
      className="h-11 border-b border-border-subtle flex items-center justify-between gap-3 px-4"
      data-testid="app-shell-header"
    >
      <ProfileContextChip />
      <BellWithDrawer orgId={feedOrgId} scopePending={orgScopePending}
        forensicPauseActive={forensicPauseActive} />
    </header>
  );
}
